import type { ReportRow } from '@/api/keywordMetrics';
import type { EntityGraph, WorkspaceResolutionContext } from './types';
import { campaignKeyForAdGroup } from './buildEntityGraph';
import {
  buildCampaignTargetingIndex,
  classifyTargetingType,
} from '@/utils/workspaceTargetingTypeMetrics';
import { classifyMatchType } from '@/utils/workspaceMatchTypeMetrics';
import {
  classifyPlacement,
  type PlacementId,
  type PlacementReportRow,
} from '@/utils/workspacePlacementMetrics';
import { campaignLookupKey } from '@/utils/workspaceAdProductMetrics';
import type { SearchTermEnrichedRow } from '@/utils/workspaceSearchTermMetrics';
import type { AdProductRow, ProductAdCatalogRow } from '@/utils/workspaceProductMetrics';

function shortAdProduct(adProduct: string): string {
  const u = adProduct.toUpperCase();
  if (u === 'SB' || u === 'SPONSORED_BRANDS' || u.includes('BRAND')) return 'SB';
  if (u === 'SD' || u === 'SPONSORED_DISPLAY' || u.includes('DISPLAY')) return 'SD';
  return 'SP';
}

export function graphCampaignKey(
  graph: EntityGraph,
  adProduct: string,
  campaignId: string
): string {
  const short = shortAdProduct(adProduct);
  const key = campaignKeyForAdGroup(short, campaignId);
  if (graph.indices.campaignsByKey.has(key)) return key;
  const found = graph.campaigns.find((c) => c.campaignId === campaignId);
  return found?.campaignKey ?? key;
}

function addToBucket(map: Map<string, Set<string>>, bucket: string, campaignKey: string) {
  let set = map.get(bucket);
  if (!set) {
    set = new Set();
    map.set(bucket, set);
  }
  set.add(campaignKey);
}

export function buildCampaignKeysByTargetingType(
  graph: EntityGraph,
  keywordRows: ReportRow[],
  targetRows: ReportRow[]
): Map<string, Set<string>> {
  const index = buildCampaignTargetingIndex(graph.campaigns);
  const out = new Map<string, Set<string>>();
  const ingest = (row: ReportRow, defaultAdProduct: string) => {
    const campaignId = row.campaignId ? String(row.campaignId) : '';
    if (!campaignId) return;
    const adProduct = row.adProduct ? String(row.adProduct) : defaultAdProduct;
    const bucket = classifyTargetingType({ ...row, adProduct }, index);
    if (!bucket) return;
    addToBucket(out, bucket, graphCampaignKey(graph, adProduct, campaignId));
  };
  for (const row of keywordRows) ingest(row, 'sponsored_products');
  for (const row of targetRows) ingest(row, row.adProduct ? String(row.adProduct) : 'sponsored_display');
  return out;
}

export function buildCampaignKeysByMatchType(
  graph: EntityGraph,
  keywordRows: ReportRow[],
  targetRows: ReportRow[]
): Map<string, Set<string>> {
  const index = buildCampaignTargetingIndex(graph.campaigns);
  const out = new Map<string, Set<string>>();
  const ingest = (row: ReportRow, defaultAdProduct: string) => {
    const campaignId = row.campaignId ? String(row.campaignId) : '';
    if (!campaignId) return;
    const adProduct = row.adProduct ? String(row.adProduct) : defaultAdProduct;
    const bucket = classifyMatchType({ ...row, adProduct }, index);
    if (!bucket) return;
    addToBucket(out, bucket, graphCampaignKey(graph, adProduct, campaignId));
  };
  for (const row of keywordRows) ingest(row, 'sponsored_products');
  for (const row of targetRows) ingest(row, row.adProduct ? String(row.adProduct) : 'sponsored_display');
  return out;
}

export function buildCampaignKeysByPlacement(
  graph: EntityGraph,
  placementByCampaignLookup: Map<string, Map<PlacementId, unknown>>
): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  for (const [lookup, byPlacement] of placementByCampaignLookup) {
    const sep = lookup.indexOf('::');
    if (sep < 0) continue;
    const adProduct = lookup.slice(0, sep);
    const campaignId = lookup.slice(sep + 2);
    const campaignKey = graphCampaignKey(graph, adProduct, campaignId);
    for (const [placementId, metrics] of byPlacement) {
      const cost =
        metrics && typeof metrics === 'object' && 'cost' in metrics
          ? Number((metrics as { cost: number }).cost)
          : 0;
      const impressions =
        metrics && typeof metrics === 'object' && 'impressions' in metrics
          ? Number((metrics as { impressions: number }).impressions)
          : 0;
      if (cost <= 0 && impressions <= 0) continue;
      addToBucket(out, placementId, campaignKey);
    }
  }
  return out;
}

export function buildCampaignKeysByPlacementFromRows(
  graph: EntityGraph,
  rows: PlacementReportRow[]
): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  for (const row of rows) {
    const campaignId = row.campaignId ? String(row.campaignId) : '';
    if (!campaignId) continue;
    const adProduct = row.adProduct ? String(row.adProduct) : 'sponsored_products';
    const bucket = classifyPlacement(row.placement);
    if (!bucket) continue;
    addToBucket(out, bucket, graphCampaignKey(graph, adProduct, campaignId));
  }
  return out;
}

export function buildSearchTermsByKey(
  rows: SearchTermEnrichedRow[]
): NonNullable<WorkspaceResolutionContext['searchTermsByKey']> {
  const out: NonNullable<WorkspaceResolutionContext['searchTermsByKey']> = new Map();
  for (const row of rows) {
    out.set(row.rowKey, {
      campaignId: row.campaignId,
      adGroupId: row.adGroupId,
      adProduct: row.adProduct,
    });
  }
  return out;
}

export function buildAdProductRowsByKey(
  rows: AdProductRow[]
): NonNullable<WorkspaceResolutionContext['adProductRowsByKey']> {
  const out: NonNullable<WorkspaceResolutionContext['adProductRowsByKey']> = new Map();
  for (const row of rows) {
    const adProduct = row.rowKey.includes('|') ? row.rowKey.split('|')[0] : 'SP';
    out.set(row.rowKey, {
      campaignId: row.campaignId,
      asin: row.asin,
      adProduct,
    });
  }
  return out;
}

export function buildAdProductRowsByKeyFromCatalog(
  catalog: ProductAdCatalogRow[]
): NonNullable<WorkspaceResolutionContext['adProductRowsByKey']> {
  const out: NonNullable<WorkspaceResolutionContext['adProductRowsByKey']> = new Map();
  for (const row of catalog) {
    const rowKey = `${row.adProduct}|${row.adId}`;
    out.set(rowKey, {
      campaignId: row.campaignId,
      asin: row.asin,
      adProduct: row.adProduct,
    });
  }
  return out;
}

export function buildWorkspaceResolutionContext(input: {
  graph: EntityGraph;
  keywordRows?: ReportRow[];
  targetRows?: ReportRow[];
  placementByCampaign?: Map<string, Map<PlacementId, unknown>>;
  searchTerms?: SearchTermEnrichedRow[];
  productAdCatalog?: ProductAdCatalogRow[];
  adProductRows?: AdProductRow[];
}): WorkspaceResolutionContext {
  const {
    graph,
    keywordRows = [],
    targetRows = [],
    placementByCampaign,
    searchTerms = [],
    productAdCatalog,
    adProductRows = [],
  } = input;

  const adProductRowsByKey = productAdCatalog?.length
    ? buildAdProductRowsByKeyFromCatalog(productAdCatalog)
    : buildAdProductRowsByKey(adProductRows);

  return {
    campaignKeysByTargetingType: buildCampaignKeysByTargetingType(graph, keywordRows, targetRows),
    campaignKeysByMatchType: buildCampaignKeysByMatchType(graph, keywordRows, targetRows),
    campaignKeysByPlacement: placementByCampaign
      ? buildCampaignKeysByPlacement(graph, placementByCampaign)
      : undefined,
    productAdCatalog: productAdCatalog?.map((r) => ({
      asin: r.asin,
      campaignId: r.campaignId,
      adProduct: r.adProduct,
    })),
    searchTermsByKey: buildSearchTermsByKey(searchTerms),
    adProductRowsByKey,
  };
}

/** @internal helper export for tests */
export function reportCampaignLookupKey(adProduct: string, campaignId: string): string {
  return campaignLookupKey(adProduct, campaignId);
}
